import React from "react";
import { motion } from "framer-motion";
import { Heart, Users, ShieldCheck, Handshake } from "lucide-react";
import PageHero from "../components/PageHero";
import SectionHeader from "../components/SectionHeader";
import { useLanguage } from "../contexts/LanguageContext";
import { IMAGES } from "../data/site";

const valueIcons = [Heart, Users, ShieldCheck, Handshake];

export default function About() {
  const { t } = useLanguage();

  return (
    <div data-testid="about-page">
      <PageHero
        eyebrow={t.about.eyebrow}
        title={t.about.title}
        image={IMAGES.community}
      />

      {/* WHO WE ARE */}
      <section className="py-24">
        <div className="gk-container grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <SectionHeader eyebrow={t.about.eyebrow} title={t.about.whoTitle} />
            <p className="mt-6 text-neutral-700 text-lg leading-relaxed">{t.about.whoBody}</p>
          </div>
          <motion.div
            className="lg:col-span-6 order-1 lg:order-2"
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7 }}
          >
            <img
              src={IMAGES.humanitarian}
              alt="Community gathering"
              className="w-full h-[480px] object-contain bg-[#0B5D1E]/5 rounded-[2rem] shadow-xl"
            />
          </motion.div>
        </div>
      </section>

      {/* MISSION + VISION */}
      <section className="py-24 bg-[#0B5D1E]/[0.03]">
        <div className="gk-container grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-3xl p-10 border border-black/5 shadow-sm" data-testid="mission-card">
            <span className="text-xs tracking-[0.25em] font-bold uppercase text-[#B5651D]">01</span>
            <h3 className="mt-3 font-heading font-bold text-3xl text-neutral-900">{t.about.missionTitle}</h3>
            <p className="mt-5 text-neutral-700 leading-relaxed">{t.about.missionBody}</p>
          </div>
          <div className="bg-[#0B5D1E] rounded-3xl p-10 text-white shadow-xl" data-testid="vision-card">
            <span className="text-xs tracking-[0.25em] font-bold uppercase text-[#E8F1EA]">02</span>
            <h3 className="mt-3 font-heading font-bold text-3xl">{t.about.visionTitle}</h3>
            <p className="mt-5 text-white/85 leading-relaxed">{t.about.visionBody}</p>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-24">
        <div className="gk-container">
          <SectionHeader
            eyebrow={t.about.eyebrow}
            title={t.about.valuesTitle}
            align="center"
          />
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {t.about.values.map((v, i) => {
              const Icon = valueIcons[i];
              return (
                <div key={i} className="gk-card" data-testid={`value-${i}`}>
                  <div className="h-12 w-12 rounded-2xl bg-[#B5651D]/10 text-[#B5651D] flex items-center justify-center mb-4">
                    <Icon size={22} />
                  </div>
                  <h4 className="font-heading font-bold text-lg">{v.title}</h4>
                  <p className="text-sm text-neutral-600 mt-2">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </div>
  );
}
